import { authenticateAndSetDetails } from "@/api/auth.service";
import ConfirmationDialog from "@/dialogs/ConfirmationDialog.vue";
import SessionExpiringSoonDialog from "@/dialogs/SessionExpiringSoonDialog.vue";
import { useDialogStore } from "@/stores/dialog";
import { useUserStore } from "@/stores/user";
import { storeToRefs } from "pinia";
import { markRaw, onBeforeUnmount, watch } from "vue";
import { createConsumer } from "@rails/actioncable";

// global
const activeConsumer = {
  consumer: null,
  subscriptionsToKeepAlive: []
};
export const useActionCable = () => {
  const { id } = storeToRefs(useUserStore());

  function resetActionCable() {
    if (activeConsumer.consumer) {
      // ActionCable does not re-check connection details, so auth data is frozen once the connection occurs
      activeConsumer.consumer.disconnect();
      activeConsumer.consumer.subscriptions.subscriptions.forEach(s => {
        activeConsumer.consumer.subscriptions.remove(s);
      });
      activeConsumer.consumer.connect();
      activeConsumer.subscriptionsToKeepAlive.forEach(s => s.cb());
    } else {
      activeConsumer.consumer = createConsumer(
        `${import.meta.env.VITE_WS_URL}/cable`
      );
    }
  }

  watch(id, resetActionCable, { immediate: true });
  onBeforeUnmount(resetActionCable);

  return activeConsumer.consumer;
};

export const addKeepAliveChannel = (cb, key) => {
  if (activeConsumer.subscriptionsToKeepAlive.some(k => k.key === key)) return;
  activeConsumer.subscriptionsToKeepAlive.push({ cb, key });
  cb();
};

export const useAuthEvents = router => {
  let signOutHandle;
  let warnHandle;

  const user = useUserStore();
  const dialog = useDialogStore();

  const browserIdCookie = document.cookie
    .split("; ")
    .find(c => c.startsWith("browser_id"));
  if (!browserIdCookie) return;
  const browserId = browserIdCookie.split("=")[1];

  let reactingToAuthStatus = false;

  async function handleAuthStatus(status) {
    reactingToAuthStatus = true;
    if (status === true) await signInEvent();
    else if (status === false) await signOutEvent();
    else await otpVerificationEvent();
    reactingToAuthStatus = false;
  }

  // NOTE(tim): This flow happens when the user hasn't 2FA'd yet
  async function otpVerificationEvent() {
    await authenticateAndSetDetails();

    const nextUrl = {
      name: user.nextAuthStep()
    };
    const nextStep = router.resolve(nextUrl);

    let redirect =
      router.currentRoute.value.query.redirect ||
      router.currentRoute.value.fullPath;

    if (nextStep.fullPath !== redirect) {
      nextUrl.query = { redirect };
    }

    return router.replace(nextUrl);
  }

  async function signInEvent() {
    if (signOutHandle) signOutHandle.close();
    if (warnHandle) warnHandle.close();

    // this guard prevents keeping the session infinitely alive
    if (user.id) return;

    await authenticateAndSetDetails();
    if (
      [
        "SignIn",
        "TwoFactorConfirmation",
        "TwoFactorEnrollmentRequired"
      ].includes(router.currentRoute.value.name)
    ) {
      const redirectTo = router.currentRoute.value.query?.redirect;
      if (redirectTo && redirectTo !== router.currentRoute.value.fullPath) {
        location.replace(redirectTo);
      } else {
        location.reload();
      }
    }
    signOutHandle = null;
  }

  async function signOutEvent() {
    if (!user.id) return;

    await user.logoutUser(router, true);

    if (warnHandle) warnHandle.close();
    if (signOutHandle) return;

    signOutHandle = dialog.showDialog({
      component: markRaw(ConfirmationDialog),
      returnHandle: true,
      title: "You have been signed out",
      subtitle:
        "You've been signed out of BOSS. BOSS sessions in all tabs have been signed out.",
      confirmText: "Close",
      hideCancel: true
    });
  }

  async function handleExpiringSoon(subscription, ttl) {
    if (!user.id) return;
    if (signOutHandle) return;
    const expiringSoon = ttl < 60 * 15;
    if (warnHandle) {
      warnHandle.close();
      warnHandle = null;
    }

    if (expiringSoon) {
      warnHandle = dialog.showDialog({
        component: markRaw(SessionExpiringSoonDialog),
        returnHandle: true,
        timeInSeconds: ttl,
        stillPresentFunc: async () => {
          await authenticateAndSetDetails();
          subscription.perform("dismiss_warning");
        },
        expiredFunc: signOutEvent
      });
    }
  }

  const subscription = activeConsumer.consumer.subscriptions.create(
    {
      channel: "BrowserChannel",
      browser_id: browserId
    },
    {
      connected() {
        if (reactingToAuthStatus) return;
        subscription.perform("current_auth_status");
      },
      received(data) {
        if (data.event == "auth_status") {
          handleAuthStatus(data.value);
        } else if (data.event == "expires_in") {
          handleExpiringSoon(subscription, data.value);
        } else if (data.event == "dismiss_warning") {
          if (warnHandle) warnHandle.close();
        }
      }
    }
  );
};
